import { motion } from "framer-motion";
import { Mail, MapPin, MessageCircle, Send, Phone, ShieldCheck } from "lucide-react";
import { useI18n, pick } from "@/lib/i18n";
import { useContent } from "@/lib/content";

export default function Contacts() {
  const { lang } = useI18n();
  const { content } = useContent();
  const c = content?.contacts;
  if (!c) return null;
  const h = pick(c, lang, c.ru);
  const aboutBg = "https://static.prod-images.emergentagent.com/jobs/4e237a5f-f05e-4566-89b2-7957aff0dcf1/images/1d53b510f49d09435613bdacc11dd9445cb7462a2c924c813ad7193e183e5e28.png";

  return (
    <section id="contacts" className="relative py-16 sm:py-20 lg:py-32 overflow-hidden" data-testid="contacts-section">
      <div className="absolute inset-0 -z-10">
        <img src={aboutBg} alt="" className="w-full h-full object-cover opacity-15 dark:opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background/85 to-background" />
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-10 sm:mb-12 lg:mb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent/10 text-accent text-xs font-semibold mb-4">
            {lang === "ru" ? "Связаться" : "Get in touch"}
          </div>
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-5xl font-semibold tracking-tight">{h.title}</h2>
          <p className="mt-4 text-base text-muted-foreground">{h.subtitle}</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-5 lg:gap-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-5 sm:p-7 lg:p-10 rounded-3xl border border-border bg-card space-y-3"
            data-testid="contact-channels"
          >
            <a href={c.whatsapp_link} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-4 p-4 rounded-2xl hover:bg-muted/60 transition-colors" data-testid="contact-whatsapp">
              <div className="size-12 rounded-2xl bg-foreground text-background flex items-center justify-center">
                <Phone className="size-5" />
              </div>
              <div className="flex-1">
                <div className="text-xs text-muted-foreground">WhatsApp</div>
                <div className="text-base font-semibold mt-0.5">{c.whatsapp}</div>
              </div>
            </a>
            <a href={c.telegram_link} target="_blank" rel="noopener noreferrer" className="group flex items-center gap-4 p-4 rounded-2xl hover:bg-muted/60 transition-colors" data-testid="contact-telegram">
              <div className="size-12 rounded-2xl bg-foreground text-background flex items-center justify-center">
                <Send className="size-5" />
              </div>
              <div className="flex-1">
                <div className="text-xs text-muted-foreground">Telegram</div>
                <div className="text-base font-semibold mt-0.5">{c.telegram}</div>
              </div>
            </a>
            <a href={`mailto:${c.email}`} className="group flex items-center gap-4 p-4 rounded-2xl hover:bg-muted/60 transition-colors" data-testid="contact-email">
              <div className="size-12 rounded-2xl bg-foreground text-background flex items-center justify-center">
                <Mail className="size-5" />
              </div>
              <div className="flex-1">
                <div className="text-xs text-muted-foreground">Email</div>
                <div className="text-base font-semibold mt-0.5 break-all">{c.email}</div>
              </div>
            </a>
            <div className="flex items-start gap-4 p-4 rounded-2xl">
              <div className="size-12 rounded-2xl bg-foreground text-background flex items-center justify-center shrink-0">
                <MapPin className="size-5" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">{lang === "ru" ? "Офис" : "Office"}</div>
                <div className="text-base mt-0.5">{c.address}</div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ delay: 0.1 }}
            className="relative p-5 sm:p-7 lg:p-10 rounded-3xl border border-border bg-foreground text-background overflow-hidden"
          >
            <div className="absolute -bottom-32 -right-32 w-72 h-72 rounded-full bg-accent/30 blur-3xl" />
            <div className="relative">
              <h3 className="font-heading text-2xl lg:text-3xl font-semibold mb-3">{c.company_name}</h3>
              <p className="text-base opacity-80 mb-8 max-w-md leading-relaxed">{c.company_desc}</p>
              <a
                href={content?.license?.file_url || "/initial/docs/license.pdf"}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-accent text-accent-foreground font-semibold text-sm hover:translate-y-[-2px] transition-transform"
                data-testid="contacts-license-btn"
              >
                <ShieldCheck className="size-4" />
                {h.license_btn}
              </a>
              <div className="mt-12 text-xs opacity-60">{c.copyright}</div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
